// Die Gegebenheiten am Dach (E3c, S4 – PLAN.md, „E3c“; ANFORDERUNGEN.md
// H31, H32): was die Dachpartie vorher wissen soll – wie hoch es bis zur
// Dachrinne ist, wie sie hinaufkommt, und ein Satz von draußen. Die ersten
// echten Fachfragen (O4): Sie stehen im schon vorbereiteten Feld
// `station.fachfragen` der Station „dach“ (server/src/db/schema.ts).
//
// Der Vertrag steht hier, bevor es die Eingabe gibt: S4 zeigt sie in der
// Karte „Dachpartie“ und auf der Dach-Unterlage, S5 baut die Eingabe in der
// HE-Doku – freiwillig, nichts muss; nachreichen geht wie bei „Sonstiges“
// (Prüfung des Plans E3c, Befund 10). Fehlt etwas, steht „nicht erfasst“ da.
//
// Die Namen der Felder sind der Vertrag mit S5 und mit den Geräten – sie
// ändern sich nicht mehr, Werte kommen nur dazu:
//   hoehe_m   die Höhe bis zur Dachrinne in Metern, ungefähr – 0 bis 30, eine Kommastelle
//   zugang    leiter | geruest | hubsteiger | unbekannt („weiß nicht“)
//   hinweis   ein Satz für die Dachpartie – höchstens 500 Zeichen

import * as z from "zod";

export const DACH_GRENZEN = {
  /** Die Höhe bis zur Dachrinne – mehr als 30 m baut niemand von uns. */
  hoeheMax: 30,
  hinweis: 500,
} as const;

/** Wie die Dachpartie hinaufkommt – „unbekannt“ ist „weiß nicht“ (H32). */
export const DACH_ZUGAENGE = ["leiter", "geruest", "hubsteiger", "unbekannt"] as const;
export type DachZugang = (typeof DACH_ZUGAENGE)[number];

/** Der Zugang in Worten – so steht er in der Karte, auf der Dach-Unterlage und (ab S5) in der HE-Doku. */
export const DACH_ZUGANG_NAMEN: Record<DachZugang, string> = {
  leiter: "Leiter reicht",
  geruest: "Gerüst nötig",
  hubsteiger: "Hubsteiger nötig",
  unbekannt: "weiß nicht",
};

/** Eine Zahl mit höchstens einer Kommastelle (6, 6,5 – nicht 6,55). */
const eineKommastelle = (n: number) => Math.abs(Math.round(n * 10) - n * 10) < 1e-9;

/** Die Gegebenheiten am Dach, wie sie gelesen werden – jedes Feld freiwillig. */
export interface DachFakten {
  hoehe_m?: number;
  zugang?: DachZugang;
  hinweis?: string;
}

/**
 * Die Gegebenheiten am Dach – jedes Feld freiwillig. Ein leerer Hinweis zählt
 * als „nicht angegeben“ und fällt weg. Unbekannte Felder fallen weg (zod:
 * strip) – so liest ein älterer Stand, was ein neuerer schreibt.
 */
export const dachFaktenVertrag = z
  .object({
    hoehe_m: z
      .number("Bitte die Höhe in Metern.")
      .min(0, "Nicht weniger als 0 m.")
      .max(DACH_GRENZEN.hoeheMax, `Höchstens ${DACH_GRENZEN.hoeheMax} m.`)
      .refine(eineKommastelle, "Höchstens eine Kommastelle.")
      .optional(),
    zugang: z.enum(DACH_ZUGAENGE).optional(),
    hinweis: z.string().trim().max(DACH_GRENZEN.hinweis, `Höchstens ${DACH_GRENZEN.hinweis} Zeichen.`).optional(),
  })
  .transform(
    (f): DachFakten => ({
      ...(f.hoehe_m !== undefined ? { hoehe_m: f.hoehe_m } : {}),
      ...(f.zugang !== undefined ? { zugang: f.zugang } : {}),
      ...(f.hinweis ? { hinweis: f.hinweis } : {}),
    }),
  );

/** Steht überhaupt etwas da? */
export function dachFaktenDa(f: DachFakten | null | undefined): f is DachFakten {
  return f !== null && f !== undefined && (f.hoehe_m !== undefined || f.zugang !== undefined || f.hinweis !== undefined);
}

/**
 * Die Gegebenheiten aus dem Feld `fachfragen` einer Station – nur der Station
 * „dach“. Was nicht zum Vertrag passt (ein alter oder kaputter Wert), zählt
 * als nicht erfasst: null. Leer (nichts erfasst): ebenfalls null.
 */
export function dachFaktenAus(station: { art: string; fachfragen: unknown } | null | undefined): DachFakten | null {
  if (!station || station.art !== "dach" || station.fachfragen === null || typeof station.fachfragen !== "object") return null;
  const gelesen = dachFaktenVertrag.safeParse(station.fachfragen);
  return gelesen.success && dachFaktenDa(gelesen.data) ? gelesen.data : null;
}

/** „ca. 6 m“ oder „ca. 6,5 m“ – die Höhe, wie man sie sagt (ungefähr, H32). */
export function dachHoeheText(m: number): string {
  return `ca. ${String(Math.round(m * 10) / 10).replace(".", ",")} m`;
}

/**
 * Die Gegebenheiten in einer Zeile – „Höhe bis zur Dachrinne ca. 6 m ·
 * Zugang: Gerüst nötig“; der Hinweis kommt nicht hinein (er steht für sich).
 * Nichts erfasst: „nicht erfasst“.
 */
export function dachFaktenZeile(f: DachFakten | null | undefined): string {
  if (!f) return "nicht erfasst";
  const teile = [
    f.hoehe_m !== undefined ? `Höhe bis zur Dachrinne ${dachHoeheText(f.hoehe_m)}` : null,
    f.zugang !== undefined ? `Zugang: ${DACH_ZUGANG_NAMEN[f.zugang]}` : null,
  ].filter((t): t is string => t !== null);
  return teile.length > 0 ? teile.join(" · ") : "Höhe und Zugang nicht erfasst";
}

/** Sagen zwei Angaben dasselbe? Nichts erfasst (null, undefined, {}) ist gleich nichts erfasst – für „hat sich etwas geändert?“ am Gerät (S5). */
export function dachFaktenGleich(a: DachFakten | null | undefined, b: DachFakten | null | undefined): boolean {
  const x = dachFaktenDa(a) ? a : {};
  const y = dachFaktenDa(b) ? b : {};
  return x.hoehe_m === y.hoehe_m && x.zugang === y.zugang && (x.hinweis ?? "") === (y.hinweis ?? "");
}
